import {
  CriticalTaskPermit,
  CriticalTaskPermitCreateData,
  CriticalTaskPermitUpdateData,
  CriticalTaskPermitFilters,
  CriticalTaskPermitStats,
  PaginatedCriticalTaskPermits,
  ApiResponse,
} from 'shared/types';
import { ApprovalChainEntry } from 'shared/types';
import { AvailableDelegate } from './initialEvaluationApi';
import api, { rawFetchWithAuth } from './apiClient';

// Audit entry shape returned by the critical-task-permit audit endpoint.
export interface CriticalTaskPermitAuditEntry {
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  changedBy: string | { _id: string; firstName?: string; lastName?: string; email?: string };
  changedAt: string;
  ip?: string;
}

class CriticalTaskPermitApi {
  // --- CRUD ---

  static async create(data: CriticalTaskPermitCreateData): Promise<CriticalTaskPermit> {
    const result = await api.post('/critical-task-permits', data);
    return result.data ?? result;
  }

  static async createFromTemplate(templateId: string, data?: Partial<CriticalTaskPermitCreateData>): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/template/${templateId}`, data || {});
    return result.data ?? result;
  }

  static async duplicate(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/duplicate`);
    return result.data ?? result;
  }

  static async getAll(filters?: CriticalTaskPermitFilters): Promise<PaginatedCriticalTaskPermits> {
    const params: Record<string, unknown> = {};
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params[key] = value;
        }
      });
    }

    const apiResponse: ApiResponse<CriticalTaskPermit[]> = await api.get('/critical-task-permits', params);
    return {
      data: apiResponse.data || [],
      total: apiResponse.pagination?.total || 0,
      page: apiResponse.pagination?.page || 1,
      limit: apiResponse.pagination?.limit || 20,
      totalPages: apiResponse.pagination?.pages || 1,
    };
  }

  static async getBatch(params: {
    include?: string;
    company?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PaginatedCriticalTaskPermits> {
    const cleanParams: Record<string, unknown> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        cleanParams[key] = value;
      }
    });

    const apiResponse: ApiResponse<CriticalTaskPermit[]> = await api.get('/critical-task-permits/batch', cleanParams);
    return {
      data: apiResponse.data || [],
      total: apiResponse.pagination?.total || 0,
      page: apiResponse.pagination?.page || 1,
      limit: apiResponse.pagination?.limit || 20,
      totalPages: apiResponse.pagination?.pages || 1,
    };
  }

  static async getById(id: string): Promise<CriticalTaskPermit> {
    const result = await api.get(`/critical-task-permits/${id}`);
    return result.data ?? result;
  }

  static async update(id: string, data: CriticalTaskPermitUpdateData): Promise<CriticalTaskPermit> {
    const result = await api.put(`/critical-task-permits/${id}`, data);
    return result.data ?? result;
  }

  // --- Approval Workflow ---

  static async submitForApproval(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/submit`);
    return result.data ?? result;
  }

  static async approve(id: string, data?: { signature?: string; notes?: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/approve`, data || {});
    return result.data ?? result;
  }

  static async reject(id: string, data: { reason: string; category: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/reject`, data);
    return result.data ?? result;
  }

  static async partialApproval(id: string, data: { notes: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/partial`, data);
    return result.data ?? result;
  }

  static async resolvePartial(id: string, data?: { notes?: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/resolve-partial`, data || {});
    return result.data ?? result;
  }

  static async delegate(id: string, data: { toApproverId: string; reason?: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/delegate`, data);
    return result.data ?? result;
  }

  // --- Checklist ---

  static async markChecklistItem(
    id: string,
    data: { sectionId: string; itemId: string; evidenceImageUrl?: string }
  ): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/checklist`, data);
    return result.data ?? result;
  }

  static async unmarkChecklistItem(
    id: string,
    data: { sectionId: string; itemId: string }
  ): Promise<CriticalTaskPermit> {
    const result = await api.delete(`/critical-task-permits/${id}/checklist`, { data });
    return result.data ?? result;
  }

  static async getEvidenceUploadUrl(
    id: string,
    contentType: string
  ): Promise<{ success: boolean; data: { presignedPost: { url: string; fields: Record<string, string> }; key: string } }> {
    return api.post(`/critical-task-permits/${id}/checklist/evidence/upload`, { contentType });
  }

  static async getChecklistImageUploadUrl(
    contentType: string
  ): Promise<{ success: boolean; data: { presignedPost: { url: string; fields: Record<string, string> }; key: string } }> {
    return api.post('/critical-task-permits/checklist/image/upload', { contentType });
  }

  static async getEquipmentImageUploadUrl(
    contentType: string
  ): Promise<{ success: boolean; data: { presignedPost: { url: string; fields: Record<string, string> }; key: string } }> {
    return api.post('/critical-task-permits/equipment/image/upload', { contentType });
  }

  static async uploadToS3(
    presignedPost: { url: string; fields: Record<string, string> },
    file: File
  ): Promise<void> {
    const formData = new FormData();
    Object.entries(presignedPost.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    const response = await fetch(presignedPost.url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Error al subir imagen a S3');
    }
  }

  static async getImageDownloadUrl(
    key: string
  ): Promise<{ success: boolean; data: { url: string } }> {
    return api.get('/files/download-url', { key });
  }

  // --- Lifecycle ---

  static async logArrival(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/arrival`);
    return result.data ?? result;
  }

  static async suspend(id: string, data: { reason: string }): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/suspend`, data);
    return result.data ?? result;
  }

  static async resume(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/resume`);
    return result.data ?? result;
  }

  static async complete(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/complete`);
    return result.data ?? result;
  }

  static async close(id: string): Promise<CriticalTaskPermit> {
    const result = await api.post(`/critical-task-permits/${id}/close`);
    return result.data ?? result;
  }

  static async delete(id: string): Promise<void> {
    await api.delete(`/critical-task-permits/${id}`);
  }

  // --- Stats & Audit ---

  static async getStats(companyId?: string): Promise<CriticalTaskPermitStats> {
    const params = companyId ? { company: companyId } : undefined;
    const result = await api.get('/critical-task-permits/stats', params);
    return result.data ?? result;
  }

  static async getAuditLog(id: string): Promise<CriticalTaskPermitAuditEntry[]> {
    const result = await api.get(`/critical-task-permits/${id}/audit`);
    return result.data ?? result;
  }

  // --- Approval Chain Management ---

  static async getApprovalChain(id: string): Promise<{ success: boolean; data: ApprovalChainEntry[] }> {
    return api.get(`/critical-task-permits/${id}/approval-chain`);
  }

  static async addToChain(
    id: string,
    data: { approverId: string; isRequired: boolean; isBench: boolean }
  ): Promise<{ success: boolean; data: CriticalTaskPermit }> {
    return api.post(`/critical-task-permits/${id}/approval-chain/add`, data);
  }

  static async removeFromChain(
    id: string,
    data?: { reason?: string }
  ): Promise<{ success: boolean; data: CriticalTaskPermit }> {
    return api.post(`/critical-task-permits/${id}/approval-chain/remove`, data || {});
  }

  static async getAvailableDelegates(
    id: string,
    search?: string
  ): Promise<{ success: boolean; data: AvailableDelegate[]; total: number }> {
    const params = search ? { search } : undefined;
    return api.get(`/critical-task-permits/${id}/approval-chain/available-delegates`, params);
  }

  // --- Export ---

  static async exportPdf(id: string): Promise<Blob> {
    const { config } = await import('config');
    const response = await rawFetchWithAuth(
      `${config.apiUrl}/critical-task-permits/${id}/export-pdf`,
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Error al exportar el PDF' }));
      throw new Error(error.error || error.message || 'Error al exportar el PDF');
    }

    return response.blob();
  }
}

export default CriticalTaskPermitApi;
